/**
 * useSelectedFace.ts
 * 
 * Custom hook for determining which thermal face is currently selected
 * Shared logic used by structural components and subsystem renderer
 */

import { useMemo } from "react";
import { useThermalDesign, getFaceIdFromBodyVec } from "../../../../ThermalDesignContext";
import type { ThermalFaceId } from "../../../../ThermalDesignTypes";

/**
 * Hook to get the currently selected thermal face or internal deck
 * Returns the face ID ("+X", "-X", etc.) for external faces
 * Returns the deck ID ("INT1", "INT2") for internal decks
 * Returns null if no face/deck is selected
 */
export const useSelectedFace = (): ThermalFaceId | "INT1" | "INT2" | null => {
  const { nodeSpecsOptions } = useThermalDesign();
  
  const selectedNode = nodeSpecsOptions.find((n) => n.isSelected);
  
  const selectedFace = useMemo(() => {
    if (!selectedNode) {
      return null;
    }
    
    // Handle internal nodes (INT1, INT2)
    if (selectedNode.values?.node_isInternal) {
      return selectedNode.id as "INT1" | "INT2";
    }
    
    // Handle external faces
    return getFaceIdFromBodyVec(selectedNode.values?.node_body_vec);
  }, [selectedNode]);
  
  return selectedFace;
};

/**
 * Hook to check if a specific face is currently selected
 * @param targetFace - The face to check (external face or internal deck)
 * @returns true if the target face is selected
 */
export const useFaceIsSelected = (targetFace: ThermalFaceId | "INT1" | "INT2"): boolean => {
  const selectedFace = useSelectedFace();
  return selectedFace === targetFace;
};
